import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { salarySlipService, payrollService } from "../../services";
import PageSkeleton from "../../components/ui/PageSkeleton";
import Button from "../../components/ui/Button";
import {
  Send,
  People,
  CalendarToday,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info as InfoIcon,
} from "@mui/icons-material";

const MAX_MONTH_SELECTION = 6;
const MONTH_FORMAT = "YYYY-MM";

const buildMonthOptions = () => {
  const months = [];
  const start = dayjs();
  for (let offset = 0; offset < 18; offset += 1) {
    const value = start.subtract(offset, "month");
    months.push({
      value: value.format(MONTH_FORMAT),
      label: value.format("MMMM YYYY"),
    });
  }
  return months;
};

function SendSalarySlipPage() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [results, setResults] = useState(null);

  const monthOptions = useMemo(buildMonthOptions, []);
  const employeeOptions = useMemo(
    () =>
      employees.map((employee) => ({
        ...employee,
        label: `${employee.name} ${
          employee.designation ? `(${employee.designation})` : ""
        }`.trim(),
      })),
    [employees]
  );

  useEffect(() => {
    const loadEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const { data } = await salarySlipService.getEmployeesMeta();
        setEmployees(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load employees", err);
        toast.error("Unable to load employees for salary slips");
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, []);

  const handleSelectAllEmployees = () => {
    setSelectedEmployees(employeeOptions);
  };

  const handleClearEmployees = () => {
    setSelectedEmployees([]);
  };

  const handleMonthsChange = (_event, newValue) => {
    if (newValue.length > MAX_MONTH_SELECTION) {
      toast.warn(`You can select up to ${MAX_MONTH_SELECTION} months`);
      return;
    }
    setSelectedMonths(newValue);
  };

  const handleOpenConfirmation = () => {
    if (selectedEmployees.length === 0) {
      toast.warning("Select at least one employee");
      return;
    }
    if (selectedMonths.length === 0) {
      toast.warning("Select at least one month");
      return;
    }
    setConfirmationOpen(true);
  };

  const handleSend = async () => {
    setSending(true);
    setResults(null);
    try {
      const payload = {
        employeeIds: selectedEmployees.map((employee) => employee.id),
        months: selectedMonths.map((month) => month.value),
      };

      const { data } = await payrollService.sendSalarySlips(payload);
      setResults(data);
      toast.success("Salary slips dispatched");
      setConfirmationOpen(false);
    } catch (err) {
      console.error("Failed to send salary slips", err);
      const message =
        err?.response?.data?.error || "Failed to send salary slips";
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const totalCombinations = selectedEmployees.length * selectedMonths.length;

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">
            Send Salary Slips
          </h1>
          <p className="text-gray-600">
            Select employees and months to send salary slips via email
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Employees Selection */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="card"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <People className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-primary">Employees</h3>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAllEmployees}
                  disabled={loadingEmployees || employeeOptions.length === 0}
                  className="px-3 py-1.5 text-sm font-medium text-primary rounded-xl hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Select All
                </button>
                <button
                  onClick={handleClearEmployees}
                  disabled={selectedEmployees.length === 0}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear
                </button>
              </div>
            </div>
            {loadingEmployees ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-16 bg-gray-200 rounded-2xl animate-pulse"
                  ></div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {employeeOptions.map((employee) => {
                    const isSelected = selectedEmployees.some(
                      (e) => e.id === employee.id
                    );
                    return (
                      <motion.div
                        key={employee.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedEmployees(
                              selectedEmployees.filter(
                                (e) => e.id !== employee.id
                              )
                            );
                          } else {
                            setSelectedEmployees([
                              ...selectedEmployees,
                              employee,
                            ]);
                          }
                        }}
                        className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected
                                ? "border-primary bg-primary"
                                : "border-gray-300"
                            }`}
                          >
                            {isSelected && (
                              <CheckCircle className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-700">
                              {employee.name}
                            </p>
                            {employee.designation && (
                              <p className="text-sm text-gray-500">
                                {employee.designation} •{" "}
                                {employee.department || "General"}
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                <p className="text-sm text-gray-500 text-center">
                  Selected: {selectedEmployees.length} employee(s)
                </p>
              </div>
            )}
          </motion.div>

          {/* Months Selection */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="card"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                <CalendarToday className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-primary">Months</h3>
                <p className="text-sm text-gray-500">
                  Max {MAX_MONTH_SELECTION} months
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="max-h-64 overflow-y-auto space-y-2">
                {monthOptions.map((month) => {
                  const isSelected = selectedMonths.some(
                    (m) => m.value === month.value
                  );
                  return (
                    <motion.div
                      key={month.value}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedMonths(
                            selectedMonths.filter(
                              (m) => m.value !== month.value
                            )
                          );
                        } else {
                          if (selectedMonths.length >= MAX_MONTH_SELECTION) {
                            toast.warn(
                              `You can select up to ${MAX_MONTH_SELECTION} months`
                            );
                            return;
                          }
                          setSelectedMonths([...selectedMonths, month]);
                        }
                      }}
                      className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? "border-secondary bg-secondary/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? "border-secondary bg-secondary"
                              : "border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <p className="font-semibold text-gray-700">
                          {month.label}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              <p className="text-sm text-gray-500 text-center">
                Selected: {selectedMonths.length} month(s)
              </p>
            </div>
          </motion.div>
        </div>

        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card mb-6"
        >
          <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
            Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <SummaryStat
              label="Employees"
              value={selectedEmployees.length}
              icon={<People className="w-6 h-6" />}
            />
            <SummaryStat
              label="Months"
              value={selectedMonths.length}
              icon={<CalendarToday className="w-6 h-6" />}
            />
            <SummaryStat
              label="Total PDFs"
              value={totalCombinations}
              icon={<Send className="w-6 h-6" />}
            />
          </div>
          <div className="flex flex-wrap items-center gap-4 pt-6 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={() => setResults(null)}
              disabled={!results}
            >
              Reset Results
            </Button>
            <Button
              variant="primary"
              onClick={handleOpenConfirmation}
              disabled={
                selectedEmployees.length === 0 ||
                selectedMonths.length === 0 ||
                sending
              }
              className="flex items-center gap-2"
            >
              <Send className="w-5 h-5" />
              {sending ? "Preparing…" : "Send Salary Slips"}
            </Button>
          </div>
        </motion.div>

        {/* Results */}
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card mb-6"
          >
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl mb-6">
              <div className="flex items-center gap-2 mb-2">
                <InfoIcon className="w-5 h-5 text-blue-600" />
                <p className="font-semibold text-blue-900">Summary</p>
              </div>
              <p className="text-sm text-blue-700">
                Total Employees: {results.summary.totalEmployees} | Sent:{" "}
                {results.summary.sent} | Partial: {results.summary.partial} |
                Failed: {results.summary.failed}
              </p>
            </div>
            <h3 className="text-xl font-bold text-primary mb-4">
              Detailed Results
            </h3>
            <div className="space-y-3">
              {results.results.map((item) => {
                const statusConfig = {
                  success: { color: "green", icon: CheckCircle },
                  partial: { color: "yellow", icon: Warning },
                  failed: { color: "red", icon: ErrorIcon },
                };
                const config =
                  statusConfig[item.status.toLowerCase()] ||
                  statusConfig.failed;
                const StatusIcon = config.icon;
                return (
                  <div
                    key={item.employeeId}
                    className="p-4 bg-gray-50 rounded-2xl border border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <StatusIcon
                        className={`w-5 h-5 text-${config.color}-600`}
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-700">
                          {resolveEmployeeName(item.employeeId, employees)} —{" "}
                          {item.status.toUpperCase()}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {item.reason ||
                            (item.monthsSent
                              ? item.monthsSent.join(", ")
                              : "No details available")}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {results.missingData?.length > 0 && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <Warning className="w-5 h-5 text-yellow-600" />
                  <p className="font-semibold text-yellow-900">
                    Missing Salary Slips
                  </p>
                </div>
                <div className="space-y-2">
                  {results.missingData.map((missing, index) => (
                    <div
                      key={`${missing.employeeId}-${missing.month || index}`}
                      className="text-sm text-yellow-700"
                    >
                      <p className="font-medium">
                        {resolveMissingLabel(missing, employees)}
                      </p>
                      {missing.reason && (
                        <p className="text-xs text-yellow-600 mt-1">
                          {missing.reason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Confirmation Dialog */}
        {confirmationOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6"
            >
              <h3 className="text-2xl font-bold text-primary mb-4">
                Send Salary Slips?
              </h3>
              <p className="text-gray-600 mb-6">
                This will email {selectedMonths.length} month(s) of salary slips
                to {selectedEmployees.length} employee(s).
              </p>
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Employees ({selectedEmployees.length})
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {selectedEmployees.map((employee) => (
                    <div
                      key={employee.id}
                      className="text-sm text-gray-600 p-2 bg-gray-50 rounded-lg"
                    >
                      {employee.name}{" "}
                      {employee.designation && `(${employee.designation})`}
                    </div>
                  ))}
                </div>
              </div>
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Months ({selectedMonths.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedMonths.map((month) => (
                    <span
                      key={month.value}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {month.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setConfirmationOpen(false)}
                  disabled={sending}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSend}
                  disabled={sending}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending…</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Send Now</span>
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function SummaryStat({ label, value, icon }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 text-center"
    >
      <div className="flex justify-center mb-3 text-primary">{icon}</div>
      <p className="text-4xl font-bold text-primary mb-1">{value}</p>
      <p className="text-sm text-gray-600 font-medium">{label}</p>
    </motion.div>
  );
}

const resolveEmployeeName = (employeeId, employees) => {
  const match = employees.find((employee) => employee.id === employeeId);
  return match ? match.name : employeeId;
};

const resolveMissingLabel = (missing, employees) => {
  const employeeName = resolveEmployeeName(missing.employeeId, employees);
  return missing.month
    ? `${employeeName} — ${dayjs(`${missing.month}-01`).format("MMMM YYYY")}`
    : employeeName;
};

export default SendSalarySlipPage;
